import { useState } from 'react';
import { PRESENTER_SLIDE_NAMES, MAX_NOTE_CHARS } from './PresentationBannerConstants';
import { NotesExportModal } from './PresentationNotesExport';
import { SlideRearrangePanel } from './PresentationSlideRearrange';

interface Props { onSignUp: () => void; }

const TRANSITIONS = [
  { name: 'Fade',  icon: 'ri-contrast-drop-line', desc: 'Smooth opacity blend' },
  { name: 'Slide', icon: 'ri-arrow-right-line',    desc: 'Push from right'     },
  { name: 'Zoom',  icon: 'ri-zoom-in-line',        desc: 'Scale from center'   },
  { name: 'Flip',  icon: 'ri-refresh-line',        desc: '3D card flip'        },
  { name: 'Morph', icon: 'ri-magic-line',          desc: 'Object morphing'     },
  { name: 'None',  icon: 'ri-forbid-2-line',       desc: 'Instant cut'         },
];

const ELEMENT_ANIMS = [
  { name: 'Rise Up',   icon: 'ri-arrow-up-circle-line', color: '#6366f1' },
  { name: 'Typewriter',icon: 'ri-text',                 color: '#f43f5e' },
  { name: 'Pop In',    icon: 'ri-focus-3-line',         color: '#10b981' },
  { name: 'Swing In',  icon: 'ri-clockwise-2-line',     color: '#f59e0b' },
];

export function PolishPanel({ onSignUp }: Props) {
  const [transition, setTransition]     = useState('Fade');
  const [speed, setSpeed]               = useState(50);
  const [autoPlay, setAutoPlay]         = useState(false);
  const [intervalVal, setIntervalVal]   = useState(5);
  const [activeAnim, setActiveAnim]     = useState<string | null>(null);
  const [activeNoteSlide, setActiveNoteSlide] = useState(0);
  const [notes, setNotes]               = useState<Record<number, string>>({});
  const [showNotesExport, setShowNotesExport] = useState(false);

  const currentNote = notes[activeNoteSlide] ?? '';
  const noteCount   = Object.values(notes).filter(n => n.trim().length > 0).length;

  return (
    <>
      <div className="space-y-5">
        {/* ── Transitions + animations ── */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Slide Transitions</p>
            <div className="grid grid-cols-3 gap-2">
              {TRANSITIONS.map(t => (
                <button key={t.name} onClick={() => setTransition(t.name)} className={`p-3 rounded-xl border transition-all cursor-pointer text-center ${transition === t.name ? 'border-sky-400/60 bg-sky-500/15' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
                  <i className={`${t.icon} text-xl mb-1 block ${transition === t.name ? 'text-sky-400' : 'text-slate-400'}`}></i>
                  <p className={`text-xs font-bold ${transition === t.name ? 'text-white' : 'text-slate-400'}`}>{t.name}</p>
                  <p className="text-xs text-slate-600 leading-tight mt-0.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Element Animations</p>
              <div className="grid grid-cols-2 gap-2">
                {ELEMENT_ANIMS.map(a => (
                  <button key={a.name} onClick={() => setActiveAnim(activeAnim === a.name ? null : a.name)}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all cursor-pointer ${activeAnim === a.name ? 'border-current bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                    style={activeAnim === a.name ? { borderColor: a.color + '80' } : {}}>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: a.color + '20' }}>
                      <i className={`${a.icon} text-sm`} style={{ color: a.color }}></i>
                    </div>
                    <span className="text-xs font-semibold text-slate-300">{a.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Transition Speed</p>
                <span className="text-xs font-bold text-sky-400">{speed < 33 ? 'Slow' : speed < 66 ? 'Medium' : 'Fast'}</span>
              </div>
              <input type="range" min={0} max={100} value={speed} onChange={e => setSpeed(Number(e.target.value))} className="w-full accent-sky-500 cursor-pointer" />
            </div>

            <div className="flex items-center justify-between p-3 bg-white/8 rounded-xl border border-white/10">
              <div>
                <p className="text-xs font-bold text-white">Auto-Advance Slides</p>
                <p className="text-xs text-slate-500">Every {intervalVal}s automatically</p>
              </div>
              <div className="flex items-center gap-3">
                {autoPlay && <input type="number" min={2} max={30} value={intervalVal} onChange={e => setIntervalVal(Number(e.target.value))} className="w-12 px-2 py-1 bg-white/10 border border-white/20 rounded-lg text-xs text-white text-center focus:outline-none" />}
                <button onClick={() => setAutoPlay(v => !v)} className={`w-11 h-6 rounded-full transition-all cursor-pointer relative ${autoPlay ? 'bg-sky-500' : 'bg-white/20'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${autoPlay ? 'left-5' : 'left-0.5'}`}></div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Presenter Notes ── */}
        <div className="border border-white/12 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-sky-500/15">
                <i className="ri-file-text-line text-sky-400 text-sm"></i>
              </div>
              <div>
                <p className="text-xs font-bold text-white">Presenter Notes</p>
                <p className="text-xs text-slate-500">Per-slide speaker notes for export</p>
              </div>
            </div>
            {noteCount > 0 && (
              <span className="px-2 py-0.5 bg-sky-500/20 text-sky-300 text-xs font-bold rounded-full border border-sky-500/30">
                {noteCount}/{PRESENTER_SLIDE_NAMES.length} noted
              </span>
            )}
          </div>

          <div className="grid grid-cols-5 divide-x divide-white/10">
            {/* Slide list */}
            <div className="col-span-2 overflow-y-auto max-h-52" style={{ scrollbarWidth: 'thin' }}>
              {PRESENTER_SLIDE_NAMES.map((name, i) => {
                const hasNote = !!notes[i]?.trim();
                const isActive = activeNoteSlide === i;
                return (
                  <button key={name} onClick={() => setActiveNoteSlide(i)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors cursor-pointer border-b border-white/6 last:border-0 ${isActive ? 'bg-sky-500/15' : 'hover:bg-white/5'}`}>
                    <span className={`text-xs font-extrabold w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-sky-500 text-white' : hasNote ? 'bg-sky-500/20 text-sky-400' : 'bg-white/8 text-slate-500'}`}>{i + 1}</span>
                    <div className="min-w-0">
                      <p className={`text-xs font-semibold truncate ${isActive ? 'text-white' : hasNote ? 'text-slate-300' : 'text-slate-500'}`}>{name}</p>
                      {hasNote && <p className="text-xs text-sky-400/70 truncate leading-none mt-0.5">{notes[i]!.slice(0, 28)}{notes[i]!.length > 28 ? '…' : ''}</p>}
                    </div>
                    {hasNote && !isActive && <div className="w-1.5 h-1.5 rounded-full bg-sky-400 flex-shrink-0 ml-auto"></div>}
                  </button>
                );
              })}
            </div>

            {/* Editor */}
            <div className="col-span-3 p-3 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-white">Slide {activeNoteSlide + 1} — {PRESENTER_SLIDE_NAMES[activeNoteSlide]}</p>
                {currentNote && (
                  <button onClick={() => setNotes(prev => { const n = { ...prev }; delete n[activeNoteSlide]; return n; })} className="text-xs text-slate-600 hover:text-rose-400 cursor-pointer transition-colors">Clear</button>
                )}
              </div>
              <textarea
                value={currentNote}
                onChange={e => { if (e.target.value.length > MAX_NOTE_CHARS) return; setNotes(prev => ({ ...prev, [activeNoteSlide]: e.target.value })); }}
                placeholder={`Speaker notes for "${PRESENTER_SLIDE_NAMES[activeNoteSlide]}"…\n\nTip: Bullet your key talking points.`}
                className="flex-1 w-full bg-white/5 border border-white/12 rounded-xl px-3 py-2.5 text-xs text-slate-300 placeholder-slate-600 resize-none focus:outline-none focus:ring-1 focus:ring-sky-500/40 min-h-[116px]"
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  {PRESENTER_SLIDE_NAMES.map((_, i) => (
                    <div key={i} onClick={() => setActiveNoteSlide(i)}
                      className={`w-1.5 h-1.5 rounded-full cursor-pointer transition-all ${i === activeNoteSlide ? 'bg-sky-400 scale-125' : notes[i]?.trim() ? 'bg-sky-500/50 hover:bg-sky-400' : 'bg-white/15 hover:bg-white/30'}`}
                      title={PRESENTER_SLIDE_NAMES[i]} />
                  ))}
                </div>
                <span className={`text-xs font-mono ${currentNote.length >= MAX_NOTE_CHARS ? 'text-rose-400' : 'text-slate-600'}`}>{currentNote.length}/{MAX_NOTE_CHARS}</span>
              </div>
            </div>
          </div>

          {/* Export row */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/10 bg-white/3">
            <p className="text-xs text-slate-500">{noteCount === 0 ? 'No notes yet — click a slide to start' : `${noteCount} slide${noteCount > 1 ? 's' : ''} with notes ready to export`}</p>
            <div className="flex items-center gap-2">
              {noteCount > 0 && (
                <button onClick={() => setShowNotesExport(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/8 border border-white/15 text-slate-300 text-xs font-bold rounded-lg cursor-pointer hover:bg-white/14 hover:text-white transition-all whitespace-nowrap">
                  <i className="ri-eye-line text-xs"></i>Preview
                </button>
              )}
              <button onClick={onSignUp} className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-500/20 border border-sky-500/30 text-sky-300 text-xs font-bold rounded-lg cursor-pointer hover:bg-sky-500/30 transition-all whitespace-nowrap">
                <i className="ri-download-2-line text-xs"></i>Export Notes
              </button>
            </div>
          </div>
        </div>

        {/* ── Slide Rearrange ── */}
        <SlideRearrangePanel onSignUp={onSignUp} />

        <button onClick={onSignUp} className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold rounded-xl cursor-pointer hover:opacity-90 text-sm whitespace-nowrap w-full">
          <i className="ri-check-double-line"></i>Apply to All Slides — Sign In
        </button>
      </div>

      {showNotesExport && (
        <NotesExportModal notes={notes} onClose={() => setShowNotesExport(false)} onExport={() => { setShowNotesExport(false); onSignUp(); }} />
      )}
    </>
  );
}
